import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { leadApi } from '@/services/api';

interface MobileLeadFormSectionProps {
  listingId: string;
  /** "property" | "plot" | "commercial" — used in the prefilled message only */
  listingLabel?: string;
  /** Human-readable reference code (e.g. ET-PR000123). Falls back to UUID slice. */
  referenceCode?: string;
  /** Which DB column to send the listingId to. Defaults to 'property'. */
  listingKind?: 'property' | 'plot' | 'project';
}

/**
 * Inline lead-capture form rendered near the bottom of the mobile detail page.
 * Mirrors the form inside `PropertyContactSidebar` but is laid out for full-width mobile.
 */
export function MobileLeadFormSection({ listingId, listingLabel = 'property', referenceCode, listingKind = 'property' }: MobileLeadFormSectionProps) {
  const refId = (referenceCode || `ID${listingId.slice(0, 8)}`).toUpperCase();
  const defaultMsg = `I would like to inquire about your ${listingLabel} EthioRealEstates - ${refId}. Please contact me at your earliest convenience.`;
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: defaultMsg });
  const [role, setRole] = useState('buyer');
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      await leadApi.create({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        message: form.message.trim() || undefined,
        ...(listingKind === 'plot'
          ? { plot_id: listingId }
          : listingKind === 'project'
          ? { project_id: listingId }
          : { property_id: listingId }),
        source: 'website',
      });
      toast.success('Your inquiry has been sent!');
      setForm({ name: '', phone: '', email: '', message: defaultMsg });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send inquiry. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <section id="lead-form" className="scroll-mt-[120px]">
      <Card className="border-2 border-primary/20">
        <CardContent className="p-4 sm:p-5">
          <h3 className="text-base font-bold mb-1">Send a Message</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Get in touch with the agent for more information
          </p>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Your Name <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="How should we address you?"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Phone Number <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <div className="flex items-center gap-1 border rounded-md px-2 text-sm text-muted-foreground shrink-0">
                  🇪🇹 +251
                </div>
                <Input
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Email Address</Label>
              <Input
                placeholder="Your best email address?"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Message</Label>
              <Textarea
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">I am a:</Label>
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4 flex-wrap">
                {[
                  { value: 'buyer', label: 'Buyer / Tenant' },
                  { value: 'agent', label: 'Agent' },
                  { value: 'other', label: 'Other' },
                ].map(opt => (
                  <div key={opt.value} className="flex items-center gap-1.5">
                    <RadioGroupItem value={opt.value} id={`mlf-role-${opt.value}`} />
                    <Label htmlFor={`mlf-role-${opt.value}`} className="text-xs cursor-pointer">
                      {opt.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="mlf-keep-informed"
                checked={keepInformed}
                onCheckedChange={c => setKeepInformed(!!c)}
              />
              <Label htmlFor="mlf-keep-informed" className="text-xs cursor-pointer">
                Keep me informed about similar properties.
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full h-11 font-bold gap-2"
              disabled={submitting}
            >
              <Mail className="h-4 w-4" />
              {submitting ? 'Sending...' : 'SEND EMAIL'}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              By submitting this form, you agree to our{' '}
              <span className="text-primary cursor-pointer">Terms of Use</span>.
            </p>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
